import React, { useCallback, useEffect, useState } from 'react';
import { Button, Modal, Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    getRompslompProducts, refreshRompslompProducts, sendRompslompBatch, sendRompslompProduct,
} from '../../../services/RompslompService';
import { SendConfirmModal } from './RompslompSending';
import { apiMessage } from './rompslompUtils';

const BAD = ['needs_attention', 'in_doubt'];
const eur = (cents) => `€ ${(cents / 100).toFixed(2).replace('.', ',')}`;

// Management products (Active + SKU, plus shipping) -> Rompslomp products. Create-only: this screen never updates or deletes anything in
// Rompslomp. Plain text, no badges; one table that turns into cards on narrow screens (the shared .rompslomp-table styles).
export default function RompslompProducts({ sending, reloadSending }) {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [confirm, setConfirm] = useState(null);
    const [busy, setBusy] = useState(false);
    const [batchAsk, setBatchAsk] = useState(false);

    const load = useCallback(async () => {
        try { setData((await getRompslompProducts()).data); } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); }
    }, [t]);
    useEffect(() => { load(); }, [load]);

    const refresh = async () => {
        setBusy(true);
        try {
            const res = await refreshRompslompProducts();
            setData(res.data);
            toast.success(t('rompslomp.products.refreshed', { matched: res.data.result.matched }));
        } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); } finally { setBusy(false); }
    };

    const create = async () => {
        setBusy(true);
        try {
            const res = await sendRompslompProduct(confirm.product_id, confirm.payload_hash);
            toast[res.data.result.ok ? 'success' : 'warning'](t(`rompslomp.log.status.${res.data.result.status}`));
            setConfirm(null);
        } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); } finally { setBusy(false); load(); reloadSending(); }
    };

    const batch = async () => {
        setBusy(true);
        setBatchAsk(false);
        try {
            const res = (await sendRompslompBatch('products')).data.result;
            toast[res.stopped_because ? 'warning' : 'success'](t('rompslomp.products.batchDone', { sent: res.sent, remaining: res.remaining }));
        } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); } finally { setBusy(false); load(); reloadSending(); }
    };

    if (!data) return <Spinner animation="border" />;
    const canSend = sending.can_send;
    const unlocked = !!sending.product_batch_unlocked;
    const creatable = (r) => r.status === 'not_created' && r.missing.length === 0;
    const todo = data.products.filter(creatable).length;

    const driftText = (d) => (d.field === 'price'
        ? t('rompslomp.products.driftPrice', { management: d.management, rompslomp: d.rompslomp })
        : t('rompslomp.products.driftVat', { management: d.management, rompslomp: d.rompslomp }));

    return (
        <>
            <p className="text-muted">{t('rompslomp.products.hint')}</p>
            <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
                <Button size="sm" variant="outline-secondary" disabled={busy} onClick={refresh} data-testid="refresh-products">{t('rompslomp.products.refresh')}</Button>
                <Button size="sm" variant="danger" disabled={!canSend || !unlocked || busy || todo === 0} onClick={() => setBatchAsk(true)} data-testid="batch-products"
                    title={unlocked ? '' : t('rompslomp.products.batchLockedHint')}>
                    {unlocked ? '' : <i className="fa-solid fa-lock me-1" />}{t('rompslomp.products.createAll', { max: data.batch_max })}
                </Button>
                <span className="text-muted small">{t('rompslomp.products.batchState', { state: unlocked ? t('rompslomp.sending.unlocked') : t('rompslomp.sending.locked') })}</span>
            </div>
            <Table className="rompslomp-table rompslomp-products" size="sm" hover data-testid="product-table">
                <thead><tr>
                    <th className="c-narrow">{t('rompslomp.products.sku')}</th><th className="c-main">{t('rompslomp.products.name')}</th>
                    <th className="c-narrow">{t('rompslomp.products.price')}</th><th className="c-status">{t('rompslomp.products.statusHeader')}</th>
                    <th>{t('rompslomp.products.missing')}</th><th>{t('rompslomp.products.drift')}</th><th className="c-actions" />
                </tr></thead>
                <tbody>
                    {data.products.map((r) => (
                        <tr key={r.product_id} data-testid={`product-row-${r.sku}`}>
                            <td className="c-narrow" data-label={t('rompslomp.products.sku')}><strong>{r.sku}</strong></td>
                            <td className="c-main" data-label={t('rompslomp.products.name')}>{r.name}
                                {r.is_delivery && <div className="small text-muted">{t('rompslomp.products.delivery')}</div>}
                                {r.is_delivery && r.management_rate && r.management_rate !== r.rate
                                    && <div className="small text-muted">{t('rompslomp.products.managementRate', { rate: r.management_rate, used: r.rate })}</div>}
                            </td>
                            <td className="c-narrow" data-label={t('rompslomp.products.price')}>{eur(r.price_incl_cents)}</td>
                            <td className="c-status" data-label={t('rompslomp.products.statusHeader')}>
                                <span className={BAD.includes(r.status) ? 'text-danger' : (r.status === 'verified' ? 'text-success' : '')}>{t(`rompslomp.products.status.${r.status}`)}</span>
                                {r.error && <div className="small text-danger">{r.error}</div>}
                            </td>
                            <td className="small" data-label={t('rompslomp.products.missing')}>{r.missing.length ? r.missing.join(', ') : t('rompslomp.products.none')}</td>
                            <td className="small" data-label={t('rompslomp.products.drift')}>
                                {r.drift.length ? r.drift.map((d) => <div key={d.field}>{driftText(d)}</div>) : t('rompslomp.products.none')}
                            </td>
                            <td className="c-actions">
                                <Button size="sm" variant="outline-danger" disabled={!creatable(r) || !canSend} onClick={() => setConfirm(r)} data-testid={`create-product-${r.sku}`}>
                                    {t('rompslomp.products.create')}
                                </Button>
                            </td>
                        </tr>
                    ))}
                    {data.products.length === 0 && <tr><td colSpan="7" className="text-center text-muted">{t('rompslomp.products.empty')}</td></tr>}
                </tbody>
            </Table>
            <SendConfirmModal show={!!confirm} title={confirm ? `${t('rompslomp.products.create')}: ${confirm.sku}` : ''}
                payload={confirm?.payload} hash={confirm?.payload_hash} busy={busy} onConfirm={create} onHide={() => setConfirm(null)} />
            <Modal show={batchAsk} onHide={() => setBatchAsk(false)} centered>
                <Modal.Header closeButton><Modal.Title>{t('rompslomp.products.createAll', { max: data.batch_max })}</Modal.Title></Modal.Header>
                <Modal.Body>{t('rompslomp.products.batchConfirm', { count: Math.min(todo, data.batch_max) })}</Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={() => setBatchAsk(false)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
                    <Button variant="danger" onClick={batch} data-testid="confirm-batch-products">{t('rompslomp.products.create')}</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
